import React, { useEffect, useState, useMemo } from 'react';
import { MaterialReactTable, MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { iBaseCardWithComponentProps, iBaseInnerCardProps } from "../../types";
import { CardWithBaseComponent } from "../../common/BaseCard";
import { useMessage } from "../../ContextMessage";
import UploadCollectionModal from './UploadCollectionModal';
import { getDynamicCollectionsRegistry, deleteCollection } from '../../../api/DynamicCollections';

const _DynamicUploadInnerCard: React.FC<iBaseInnerCardProps> = ({ setIsShow, setData }) => {
    const [registry, setRegistry] = useState<any[]>([]);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [collectionToDelete, setCollectionToDelete] = useState<string | null>(null);
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const { showInfo, showWarning } = useMessage();

    const loadRegistry = async () => {
        try {
            const result = await getDynamicCollectionsRegistry();
            setRegistry(result);
        } catch (error) {
            console.error('Failed to fetch registry:', error);
            showWarning('Failed to fetch registry data.');
        }
    };

    const delayedLoadRegistry = () => {
        setTimeout(() => {
            loadRegistry();
        }, 500);
    };

    const handleDelete = async (collectionName: string) => {
        try {
            await deleteCollection(collectionName);
            showInfo(`Collection '${collectionName}' deleted successfully.`);
            delayedLoadRegistry();
        } catch (error) {
            console.error('Delete failed:', error);
            showWarning('Failed to delete collection.');
        }
    };

    const openDeleteConfirmation = (collectionName: string) => {
        setCollectionToDelete(collectionName);
        setOpenDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (collectionToDelete) {
            await handleDelete(collectionToDelete);
        }
        setOpenDeleteModal(false);
        setCollectionToDelete(null);
    };

    useEffect(() => {
        loadRegistry();
        setIsShow(true);
    }, []);

    const columns: MRT_ColumnDef<any>[] = useMemo(
        () => [
            { accessorKey: 'collection_name', header: 'Collection Name' },
            { accessorKey: 'tab_name', header: 'Tab Name' },
            { accessorKey: 'update_date', header: 'Updated At' },
            { accessorKey: 'user_update', header: 'Updated By' },
            { accessorKey: 'count_rows', header: 'Rows Count' },
        ],
        []
    );

    return (
        <div>
            <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenUploadModal(true)}
                style={{ marginBottom: '1rem' }}
            >
                Upload New Collection
            </Button>

            <UploadCollectionModal
                open={openUploadModal}
                onClose={() => setOpenUploadModal(false)}
                onUploadSuccess={delayedLoadRegistry}
            />

            {/* Table of Registry ONLY if registry has items */}
            {registry.length > 0 && (
                <div className="mt-8">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                        <Tooltip title="Refresh Registry">
                            <IconButton onClick={loadRegistry} aria-label="Refresh registry">
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <MaterialReactTable
                        columns={columns}
                        data={registry}
                        enableRowActions
                        renderRowActions={({ row }: { row: MRT_Row<any> }) => (
                            <div>
                                <Tooltip title="Delete">
                                    <IconButton
                                        color="error"
                                        onClick={() => openDeleteConfirmation(row.original.collection_name)}
                                        aria-label={`Delete collection ${row.original.collection_name}`}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        )}
                        renderDetailPanel={({ row }: { row: MRT_Row<any> }) => (
                            <div style={{ padding: '1rem' }}>
                                <h4>Columns:</h4>
                                <ul>
                                    {row.original.columns && row.original.columns.length > 0 ? (
                                        row.original.columns.map((col: string) => (
                                            <li key={col}>{col}</li>
                                        ))
                                    ) : (
                                        <li>No columns available</li>
                                    )}
                                </ul>
                            </div>
                        )}
                    />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Dialog
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete the collection '{collectionToDelete}'? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteModal(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const DynamicUploadCard: React.FC<Omit<iBaseCardWithComponentProps, 'cardComponent'>> = ({
                                                                                             header,
                                                                                             width,
                                                                                             eventGroupToTrigger,
                                                                                             showBasket,
                                                                                             storageValue
                                                                                         }) => {
    return (
        <CardWithBaseComponent
            cardComponent={_DynamicUploadInnerCard}
            header={header}
            width={width}
            eventGroupToTrigger={eventGroupToTrigger}
            showBasket={showBasket}
            storageValue={storageValue}
        />
    );
};

export default React.memo(DynamicUploadCard);
